/**
 * The last good answer from SL, kept on disk.
 *
 * Worth keeping because a departure board is not like most cached data: the
 * times are absolute. A bus expected at 21:40 is still expected at 21:40 ten
 * minutes after the response was fetched, and since the countdown is computed
 * locally from that timestamp rather than taken from SL's pre-rendered text,
 * an old answer keeps telling the truth until its departures have gone.
 *
 * So when SL refuses -- a quota refusal, no signal, a phone that has just
 * woken up -- the board opens with the times it knew instead of nothing, says
 * how old they are, and quietly replaces them the moment a real answer
 * arrives. What it cannot know is what changed since: a delay announced in
 * the meantime, a cancellation, a departure added. That is the honest limit,
 * and the age in the stop's header is what admits to it.
 */
import type { Departure, DeparturesResponse, Pair } from './sl.ts';
import { matches } from './sl.ts';
import { parseStockholm } from './time.ts';

const KEY = 'sl:departures:1';

/**
 * A departure a couple of minutes past is still worth showing -- SL itself
 * lists a bus at the stop as "Nu", and one that just pulled out is a thing
 * you want to know you missed. Beyond that it is noise, and on cached data it
 * would be a row of "Nu" for services long gone.
 */
export const PAST_GRACE_MS = 2 * 60_000;

type Entry = {
  fetchedAt: number;
  departures: Departure[];
  stopDeviations: DeparturesResponse['stop_deviations'];
};

type Stored = Record<string, Entry>;

function read(): Stored {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Stored) : {};
  } catch {
    return {};
  }
}

function write(next: Stored) {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Private mode, or a full quota. The app works without this; it just
    // opens empty next time instead of opening with the last known times.
  }
}

/** Has this departure already gone, beyond the grace above? */
export const hasPassed = (d: Departure, now = Date.now()): boolean => {
  const at = parseStockholm(d.expected);
  return Number.isFinite(at) && at < now - PAST_GRACE_MS;
};

/**
 * Store only the departures for lines this stop actually tracks.
 *
 * A whole board is around 45 KB and rewriting that every couple of minutes
 * for every stop would be the most expensive thing the app does. The rows on
 * screen need at most three departures per saved line, which is a couple of
 * kilobytes.
 */
export function saveDepartures(
  siteId: number,
  response: DeparturesResponse,
  pairs: Pair[],
): void {
  if (pairs.length === 0) return;

  const departures = response.departures.filter(
    (d) => pairs.some((p) => matches(d, p)) && !hasPassed(d),
  );

  const stored = read();
  stored[String(siteId)] = {
    fetchedAt: Date.now(),
    departures,
    stopDeviations: response.stop_deviations,
  };
  write(prune(stored));
}

/**
 * What we knew about this stop, or nothing if none of it is still ahead.
 *
 * Returning nothing rather than an empty board matters: an entry whose
 * departures have all gone would otherwise render as "nothing in 3 h", which
 * is a claim about the timetable rather than an admission that we are out of
 * date.
 */
export function loadDepartures(
  siteId: number,
): { response: DeparturesResponse; fetchedAt: number } | null {
  const entry = read()[String(siteId)];
  if (!entry) return null;

  const departures = (entry.departures ?? []).filter((d) => !hasPassed(d));
  if (departures.length === 0) return null;

  return {
    fetchedAt: entry.fetchedAt,
    response: { departures, stop_deviations: entry.stopDeviations ?? [] },
  };
}

/** Drop whatever has nothing left to say, so this cannot grow without bound. */
function prune(stored: Stored): Stored {
  const next: Stored = {};
  for (const [siteId, entry] of Object.entries(stored)) {
    const departures = (entry.departures ?? []).filter((d) => !hasPassed(d));
    if (departures.length > 0) next[siteId] = { ...entry, departures };
  }
  return next;
}
